import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  FileText,
  LayoutList,
  MessageSquare,
  MoreHorizontal,
  Search,
  Send,
  Sparkles,
  Trash2,
  Upload,
  UploadCloud,
} from "lucide-react";
import Sidebar from "@/pages/home/components/Sidebar";
import { deleteKnowledgeBase, getKnowledgeBaseById, renameKnowledgeBase, upsertKnowledgeBase } from "@/pages/knowledge/store";
import { getKnowledgeItemsByKb, removeKnowledgeItemById } from "@/pages/knowledge/itemStore";
import KnowledgeGraphView from "@/pages/knowledge/components/KnowledgeGraphView";

const detailTabs = ["内容", "索引图谱", "成员"] as const;
const contentTabs = ["全部", "文献", "文档", "对话", "代码", "成果"] as const;

type DetailTab = (typeof detailTabs)[number];

const typeMap: Record<string, { label: string; badge: string }> = {
  pdf: { label: "PDF", badge: "border border-border bg-muted/35 text-muted-foreground" },
  docx: { label: "DOCX", badge: "border border-border bg-muted/35 text-muted-foreground" },
  doi: { label: "DOI", badge: "border border-border bg-muted/35 text-muted-foreground" },
  ris: { label: "RIS", badge: "border border-border bg-muted/35 text-muted-foreground" },
  arxiv: { label: "arXiv", badge: "border border-border bg-muted/35 text-muted-foreground" },
  chat: { label: "对话", badge: "border border-border bg-muted/35 text-muted-foreground" },
  social: { label: "链接", badge: "border border-border bg-muted/35 text-muted-foreground" },
  code: { label: "Code", badge: "border border-border bg-muted/35 text-muted-foreground" },
  result: { label: "成果", badge: "border border-border bg-muted/35 text-muted-foreground" },
};

const mockMembers = [
  { id: "me", name: "我", role: "所有者", email: "me@example.com" },
  { id: "m1", name: "张晓明", role: "管理员", email: "zhang@example.com" },
  { id: "m2", name: "李思远", role: "协作者", email: "li@example.com" },
  { id: "m3", name: "王博士", role: "仅查看", email: "wang@example.com" },
];

type ChatMsg = { role: "user" | "ai"; content: string; ts?: number };

function chatKey(kbId: string) {
  return `scienceone.kbChat.v1.${kbId}`;
}

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function loadChat(kbId: string): ChatMsg[] {
  const stored = safeParse<ChatMsg[]>(sessionStorage.getItem(chatKey(kbId)));
  if (stored && Array.isArray(stored) && stored.length) return stored;
  return [{ role: "ai", content: "你好！我是这个知识库的 AI 助手。你可以粘贴论文标题/问题，我会基于库内条目给出定位与建议。", ts: Date.now() }];
}

function saveChat(kbId: string, list: ChatMsg[]) {
  sessionStorage.setItem(chatKey(kbId), JSON.stringify(list));
}

function normalize(s: string) {
  return s.toLowerCase().replace(/\s+/g, " ").trim();
}

function buildMockAnswer(opts: { kbName: string; q: string; items: { title: string; description: string; tags: string[]; abstract?: string }[] }) {
  const qn = normalize(opts.q);
  const hits = opts.items
    .map((it) => {
      const hay = normalize([it.title, it.description, (it.abstract || "").slice(0, 260), it.tags.join(" ")].join(" "));
      const score = qn.split(" ").filter((w) => w && hay.includes(w)).length + (hay.includes(qn) ? 3 : 0);
      return { it, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((x) => x.it);

  const head = `我在「${opts.kbName}」里为你做了一次快速检索，问题：${opts.q}`;
  if (hits.length === 0) {
    return [
      head,
      "",
      "未命中明显相关条目。你可以试试：",
      "- 换成更具体的关键词（方法名/指标/材料/数据集）",
      "- 先导入对应论文（arXiv/DOI/PDF）再提问",
      "- 或告诉我你想要：总结 / 对比 / 复现实验步骤 / 关键结论",
    ].join("\n");
  }

  const lines: string[] = [head, "", `命中 ${hits.length} 条：`];
  hits.forEach((h, i) => {
    const snippet = (h.abstract || h.description || "").replace(/\s+/g, " ").trim().slice(0, 140);
    lines.push(`${i + 1}. ${h.title}`);
    if (snippet) lines.push(`   - 摘要要点：${snippet}${snippet.length >= 140 ? "…" : ""}`);
    if (h.tags?.length) lines.push(`   - 标签：${h.tags.slice(0, 6).map((t) => `#${t}`).join(" ")}`);
  });
  lines.push("", "下一步建议：", "- 打开命中条目的 PDF，先看 Abstract / Introduction / Conclusion 三段", "- 如果你要复现：告诉我实验设置/数据集/评价指标，我帮你列 checklist", "- 如果你要对比：告诉我要对比的两篇（或给出关键词），我帮你抽取差异点");
  return lines.join("\n");
}

export default function KnowledgeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "内容";
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailTab>(detailTabs.includes(initialTab as DetailTab) ? (initialTab as DetailTab) : "内容");
  const [activeTypeFilter, setActiveTypeFilter] = useState<(typeof contentTabs)[number]>("全部");
  const [chatInput, setChatInput] = useState("");
  const [chatHistory, setChatHistory] = useState<ChatMsg[]>([{ role: "ai", content: "你好！我是这个知识库的 AI 助手。" }]);
  const chatListRef = useRef<HTMLDivElement | null>(null);
  const chatInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [shareEnabled, setShareEnabled] = useState(false);
  const [members, setMembers] = useState(mockMembers);
  const [roleMenu, setRoleMenu] = useState<{ id: string; top: number; right: number } | null>(null);
  const [removeMemberOpen, setRemoveMemberOpen] = useState(false);
  const [removeMemberTarget, setRemoveMemberTarget] = useState<{ id: string; name: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [kbMenuOpen, setKbMenuOpen] = useState(false);
  const kbMenuRef = useRef<HTMLDivElement | null>(null);
  const kbMenuBtnRef = useRef<HTMLButtonElement | null>(null);
  const [kbMenuPos, setKbMenuPos] = useState<{ top: number; right: number } | null>(null);
  const coverInputRef = useRef<HTMLInputElement | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameVal, setRenameVal] = useState("");
  const [deleteKbOpen, setDeleteKbOpen] = useState(false);
  const [kbTick, setKbTick] = useState(0);

  const kb = useMemo(() => getKnowledgeBaseById(id) ?? getKnowledgeBaseById("kb1")!, [id, kbTick]);
  const items = getKnowledgeItemsByKb(kb.id);

  useEffect(() => {
    if (!kbMenuOpen) return;
    const close = (e: MouseEvent) => {
      if (kbMenuRef.current && !kbMenuRef.current.contains(e.target as Node)) {
        setKbMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [kbMenuOpen]);

  useEffect(() => {
    if (!roleMenu) return;
    const close = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.("[data-role-trigger='1']")) return;
      if (t?.closest?.("[data-role-menu='1']")) return;
      setRoleMenu(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [roleMenu]);

  useEffect(() => {
    if (!kbMenuOpen) return;
    const onResize = () => {
      const btn = kbMenuBtnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      setKbMenuPos({ top: r.bottom + 8, right: window.innerWidth - r.right });
    };
    onResize();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [kbMenuOpen]);

  useEffect(() => {
    setChatHistory(loadChat(kb.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kb.id]);

  useEffect(() => {
    saveChat(kb.id, chatHistory);
  }, [kb.id, chatHistory]);

  // 新消息到来：自动滚到底部（把旧消息顶上去）
  useEffect(() => {
    const el = chatListRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [chatHistory.length]);

  const filteredItems = useMemo(
    () =>
      items.filter((item) => {
        if (activeTypeFilter === "全部") return true;
        const map: Record<string, string[]> = {
          文献: ["pdf", "doi", "ris", "arxiv"],
          文档: ["docx", "social"],
          对话: ["chat"],
          代码: ["code"],
          成果: ["result"],
        };
        return map[activeTypeFilter]?.includes(item.sourceType) ?? false;
      }),
    [activeTypeFilter, items]
  );

  const sendChat = () => {
    if (!chatInput.trim()) return;
    const q = chatInput.trim();
    setChatInput("");
    setChatHistory((prev) => {
      const next = [...prev, { role: "user", content: q, ts: Date.now() }];
      const answer = buildMockAnswer({
        kbName: kb.name,
        q,
        items: items.map((it) => ({ title: it.title, description: it.description, tags: it.tags, abstract: it.abstract })),
      });
      next.push({ role: "ai", content: answer, ts: Date.now() + 1 });
      return next;
    });
  };

  const handleInvite = () => {
    if (!inviteEmail.trim()) return;
    setMembers((prev) => [...prev, { id: `m${Date.now()}`, name: inviteEmail.split("@")[0], role: "仅查看", email: inviteEmail }]);
    setInviteEmail("");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#F6F7FB] text-foreground">
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background">
        <div className="border-b border-white/60 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/60">
          <div className="mx-auto w-full max-w-[1100px] pt-8 pb-0 px-0">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate("/knowledge")} className="inline-flex items-center gap-2 rounded-full border border-border bg-white/90 px-3.5 py-2 text-sm font-medium text-muted-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground">
                <ArrowLeft className="h-4 w-4" /> 知识库
              </button>
              <span className="text-muted-foreground">/</span>
              <h1 className="truncate text-base font-semibold tracking-tight">{kb.name}</h1>
              <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">知识库</span>
            </div>

            <div className="relative z-10 mt-2 flex items-center gap-2 rounded-[24px] border border-white/70 bg-white/80 px-0 py-3 shadow-[0_8px_24px_rgba(15,23,42,0.03)]">
              {detailTabs.map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${activeTab === tab ? "bg-foreground text-background shadow-sm" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"}`}>
                  {tab}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={() => navigate("/knowledge/import")}
                  className="inline-flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-2 text-sm font-medium text-[#1D2129] transition-colors hover:bg-[#F9FAFB]"
                >
                  <Upload className="h-4 w-4" />导入内容
                </button>
                <button className="inline-flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-2 text-sm font-medium text-[#1D2129] transition-colors hover:bg-[#F9FAFB]">
                  <Sparkles className="h-4 w-4" />分享
                </button>
                <div className="relative">
                  <button
                    type="button"
                    ref={kbMenuBtnRef}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[#E5E7EB] bg-white text-[#1D2129] transition-colors hover:bg-[#F9FAFB]"
                    onClick={(e) => {
                      e.stopPropagation();
                      const next = !kbMenuOpen;
                      setKbMenuOpen(next);
                      if (next) {
                        const btn = kbMenuBtnRef.current;
                        if (btn) {
                          const r = btn.getBoundingClientRect();
                          setKbMenuPos({ top: r.bottom + 8, right: window.innerWidth - r.right });
                        }
                      }
                    }}
                    aria-label="更多"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                </div>
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    if (!file) return;
                    if (!/^image\//.test(file.type)) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      const dataUrl = String(reader.result || "");
                      upsertKnowledgeBase({
                        ...kb,
                        coverImage: dataUrl,
                        updatedAt: new Date().toISOString().replace("T", " ").slice(0, 16),
                      });
                      setKbTick((x) => x + 1);
                    };
                    reader.readAsDataURL(file);
                    if (coverInputRef.current) coverInputRef.current.value = "";
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pt-4 pb-5">
          <div className="mx-auto w-full max-w-[1100px]">
            {activeTab === "内容" && (
              <div className="flex gap-5">
                <section className="min-w-0 flex-1">
                  <div
                    className="flex flex-col overflow-hidden rounded-[28px] border border-white/70 bg-white/85 shadow-[0_12px_40px_rgba(15,23,42,0.05)] backdrop-blur"
                    style={{ height: "calc(100vh - 200px)" }}
                  >
                    <div className="flex flex-wrap items-center gap-2 border-b border-white/70 px-5 py-4">
                      {contentTabs.map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setActiveTypeFilter(tab)}
                          className={`rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${activeTypeFilter === tab ? "bg-primary text-primary-foreground" : "border border-border bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"}`}
                        >
                          {tab}
                        </button>
                      ))}
                      <div className="ml-auto flex h-10 items-center gap-2 rounded-lg border border-input bg-background px-3 shadow-sm">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        <input className="w-40 bg-transparent text-sm outline-none placeholder:text-muted-foreground" placeholder="搜索内容..." />
                      </div>
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto p-5">
                      {filteredItems.length ? (
                        <div className="space-y-3">
                          {filteredItems.map((item) => {
                            const cfg = typeMap[item.sourceType] || typeMap.pdf;
                            return (
                              <div key={item.id} className="group flex items-center gap-3 rounded-2xl border border-border bg-background p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
                                <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${cfg.badge}`}>
                                  <FileText className="h-5 w-5" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="truncate text-sm font-semibold">{item.title}</p>
                                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${cfg.badge}`}>{cfg.label}</span>
                                  </div>
                                  <p className="mt-1 line-clamp-1 text-sm text-muted-foreground">{item.description}</p>
                                </div>
                                <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                                  <button
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                                    onClick={() => {
                                      const mention = `@${item.title} `;
                                      setChatInput((prev) => (prev.trim() ? `${prev}\n${mention}` : mention));
                                      window.requestAnimationFrame(() => chatInputRef.current?.focus());
                                    }}
                                    aria-label="在对话中引用文件"
                                  >
                                    <MessageSquare className="h-4 w-4" />
                                  </button>
                                  <button
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                                    onClick={() => setDeleteTarget({ id: item.id, title: item.title })}
                                    aria-label="删除条目"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                  <button
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                                    onClick={() => navigate(`/knowledge/item/${item.id}`)}
                                    aria-label="查看条目"
                                  >
                                    <ArrowUpRight className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="h-full">
                          <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-dashed border-border bg-card p-8 text-center shadow-sm">
                            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border border-border bg-muted/35 text-foreground">
                              <UploadCloud className="h-7 w-7 text-muted-foreground" />
                            </div>
                            <div className="text-base font-medium text-foreground">这个知识库还没有内容</div>
                            <div className="mt-2 text-sm text-muted-foreground">上传 PDF/DOCX 或导入 arXiv/DOI，把你的文献和资料沉淀在这里。</div>
                            <button
                              type="button"
                              onClick={() => navigate(`/knowledge/import?kbId=${encodeURIComponent(kb.id)}`)}
                              className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:opacity-90"
                            >
                              <Upload className="h-4 w-4" />
                              去导入内容
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                <aside
                  className="flex w-[360px] shrink-0 flex-col overflow-hidden rounded-[28px] border border-white/70 bg-white/85 shadow-[0_12px_40px_rgba(15,23,42,0.05)] backdrop-blur"
                  style={{ height: "calc(100vh - 200px)" }}
                >
                  <div className="border-b border-white/70 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      <span className="text-sm font-semibold">全库对话</span>
                      <span className="ml-auto text-xs text-muted-foreground">基于 {items.length} 个文件</span>
                    </div>
                  </div>
                  <div ref={chatListRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
                    {chatHistory.map((msg, i) => (
                      <div key={i} className={`flex gap-2.5 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                        <div className={`max-w-[260px] rounded-2xl px-4 py-3 text-sm leading-6 ${msg.role === "ai" ? "bg-muted" : "bg-foreground text-background"}`}>
                          {msg.content}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-white/70 p-4">
                    <div className="rounded-2xl border border-input bg-background p-3 shadow-sm focus-within:ring-2 focus-within:ring-ring">
                      <textarea
                        ref={chatInputRef}
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            sendChat();
                          }
                        }}
                        rows={2}
                        placeholder="向知识库提问..."
                        className="w-full resize-none bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                      />
                      <div className="mt-3 flex justify-end">
                        <button onClick={sendChat} className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-foreground text-background transition-opacity hover:opacity-90">
                          <Send className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            )}

            {activeTab === "索引图谱" && (
              <div className="rounded-[28px] border border-white/70 bg-white/85 p-5 shadow-[0_12px_40px_rgba(15,23,42,0.05)] backdrop-blur">
                <KnowledgeGraphView />
              </div>
            )}

            {activeTab === "成员" && (
              <div className="space-y-4">
                <div className="rounded-[28px] border border-white/70 bg-white/85 p-5 shadow-[0_12px_40px_rgba(15,23,42,0.05)] backdrop-blur">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold">公开分享</p>
                      <p className="mt-1 text-sm text-muted-foreground">开启后任何人都可以通过链接访问此知识库。</p>
                    </div>
                    <button onClick={() => setShareEnabled(!shareEnabled)} className={`relative h-6 w-11 rounded-full ${shareEnabled ? "bg-foreground" : "bg-muted"}`}>
                      <span className={`absolute top-1 h-4 w-4 rounded-full bg-background transition-all ${shareEnabled ? "left-6" : "left-1"}`} />
                    </button>
                  </div>
                </div>

                <div className="rounded-[28px] border border-white/70 bg-white/85 p-5 shadow-[0_12px_40px_rgba(15,23,42,0.05)] backdrop-blur">
                  <div className="flex gap-2">
                    <input
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="输入邮箱地址..."
                      className="h-11 flex-1 rounded-lg border border-input bg-background px-3 text-sm outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring"
                    />
                    <button onClick={handleInvite} className="inline-flex h-11 items-center rounded-lg bg-foreground px-5 text-sm font-medium text-background">
                      邀请
                    </button>
                  </div>
                </div>

                <div className="rounded-[28px] border border-white/70 bg-white/85 shadow-[0_12px_40px_rgba(15,23,42,0.05)] backdrop-blur">
                  <div className="border-b border-white/70 px-4 py-3">
                    <span className="text-sm font-semibold">成员列表 ({members.length})</span>
                  </div>
                  <div className="divide-y divide-border">
                    {members.map((m) => (
                      <div key={m.id} className="flex items-center gap-3 px-4 py-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-sm font-semibold">{m.name[0]}</div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium">{m.name}</p>
                          <p className="text-sm text-muted-foreground">{m.email}</p>
                        </div>
                        {m.role !== "所有者" ? (
                          <button
                            type="button"
                            data-role-trigger="1"
                            onClick={(e) => {
                              e.stopPropagation();
                              const el = e.currentTarget as HTMLButtonElement;
                              const r = el.getBoundingClientRect();
                              setRoleMenu((cur) => (cur?.id === m.id ? null : { id: m.id, top: r.bottom + 8, right: window.innerWidth - r.right }));
                            }}
                            className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                            aria-label="修改成员权限"
                          >
                            {m.role}
                            <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground opacity-80">
                            所有者
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm" onClick={() => setDeleteTarget(null)}>
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold tracking-tight text-foreground">删除条目</h3>
            <p className="mt-2 text-sm text-muted-foreground">确定删除「{deleteTarget.title}」吗？该条目将从此知识库中移除（演示：本地存储）。</p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => {
                  removeKnowledgeItemById(deleteTarget.id);
                  setDeleteTarget(null);
                }}
                className="rounded-lg bg-primary px-5 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:opacity-90"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}

      {removeMemberOpen && removeMemberTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm" onClick={() => setRemoveMemberOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold tracking-tight text-foreground">移出成员</h3>
            <p className="mt-2 text-sm text-muted-foreground">确定将「{removeMemberTarget.name}」移出该知识库吗？移出后将无法继续访问（演示：本地状态）。</p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setRemoveMemberOpen(false);
                  setRemoveMemberTarget(null);
                }}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => {
                  setMembers((prev) => prev.filter((m) => m.id !== removeMemberTarget.id));
                  setRemoveMemberOpen(false);
                  setRemoveMemberTarget(null);
                }}
                className="rounded-lg bg-[#EF4444] px-5 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#DC2626]"
              >
                确认移出
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {renameOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm" onClick={() => setRenameOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold tracking-tight text-foreground">重命名知识库</h3>
            <p className="mt-2 text-sm text-muted-foreground">修改知识库名称。</p>
            <div className="mt-5">
              <input
                autoFocus
                value={renameVal}
                onChange={(e) => setRenameVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const nextName = renameVal.trim();
                    if (!nextName) return;
                    renameKnowledgeBase(kb.id, nextName);
                    setKbTick((x) => x + 1);
                    setRenameOpen(false);
                  }
                }}
                className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                placeholder="输入新名称"
              />
            </div>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setRenameOpen(false)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => {
                  const nextName = renameVal.trim();
                  if (!nextName) return;
                  renameKnowledgeBase(kb.id, nextName);
                  setKbTick((x) => x + 1);
                  setRenameOpen(false);
                }}
                className="rounded-lg bg-primary px-5 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:opacity-90"
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteKbOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm" onClick={() => setDeleteKbOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold tracking-tight text-foreground">删除知识库</h3>
            <p className="mt-2 text-sm text-muted-foreground">确定删除「{kb.name}」吗？此操作不可恢复（演示：本地存储）。</p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteKbOpen(false)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => {
                  deleteKnowledgeBase(kb.id);
                  setDeleteKbOpen(false);
                  navigate("/knowledge");
                }}
                className="rounded-lg bg-primary px-5 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:opacity-90"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}

      {kbMenuOpen && kbMenuPos ? (
        <div
          ref={kbMenuRef}
          className="fixed z-[999] min-w-44 overflow-hidden rounded-xl border border-border bg-popover p-1 shadow-lg"
          style={{ top: kbMenuPos.top, right: kbMenuPos.right }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => {
              setRenameVal(kb.name);
              setRenameOpen(true);
              setKbMenuOpen(false);
            }}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-accent"
          >
            <LayoutList className="h-4 w-4 text-muted-foreground" />
            重命名
          </button>
          <button
            type="button"
            onClick={() => {
              setKbMenuOpen(false);
              coverInputRef.current?.click();
            }}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-accent"
          >
            <Upload className="h-4 w-4 text-muted-foreground" />
            上传封面
          </button>
          <button
            type="button"
            onClick={() => {
              setKbMenuOpen(false);
              setDeleteKbOpen(true);
            }}
            className="flex w-full items-center gap-2 rounded-lg bg-white px-3 py-2.5 text-left text-sm font-medium text-destructive transition-colors hover:bg-white hover:text-destructive"
          >
            <Trash2 className="h-4 w-4 shrink-0 text-destructive" />
            删除
          </button>
        </div>
      ) : null}

      {roleMenu ? (
        <div
          data-role-menu="1"
          className="fixed z-[999] min-w-40 overflow-hidden rounded-xl border border-border bg-popover p-1 shadow-lg"
          style={{ top: roleMenu.top, right: roleMenu.right }}
          onClick={(e) => e.stopPropagation()}
        >
          {(["管理员", "协作者", "仅查看"] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => {
                setMembers((prev) => prev.map((x) => (x.id === roleMenu.id ? { ...x, role: opt } : x)));
                setRoleMenu(null);
              }}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-accent"
            >
              <span>{opt}</span>
            </button>
          ))}
          <div className="my-1 h-px bg-border" />
          <button
            type="button"
            onClick={() => {
              const m = members.find((x) => x.id === roleMenu.id);
              if (!m) return;
              if (m.role === "所有者") return;
              setRoleMenu(null);
              setRemoveMemberTarget({ id: m.id, name: m.name });
              setRemoveMemberOpen(true);
            }}
            className="flex w-full items-center justify-between rounded-lg bg-white px-3 py-2.5 text-left text-sm font-medium text-destructive transition-colors hover:bg-white hover:text-destructive"
          >
            <span>移出</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
